/**
 * Contains the Annotations static class, used to manipulate an editor's annotations
 */

import {
  createEditor,
  Editor,
  Point,
  Range,
  Transforms,
  Text,
  type BaseSelection,
  type NodeEntry,
  type Node,
} from 'slate';
import { ReactEditor } from 'slate-react';
import type {
  Annotation,
  AnnotationKey,
  AnnotationKind,
  AnnotationsProvider,
  UnreferencedAnnotation,
  RecordId,
  Marks,
  AnnotationMarks,
  ReferenceAhaObject,
} from '../../types/aha-editor';
import { cloneDeep, omit } from 'lodash';
import AhaEditor from '../../AhaEditor';
import { isAnnotationKey } from '../../types/guards';
import textInRange from '../../AhaEditorUtils/util/textInRange';
import History from '../../collaboration/History';
import { LOCAL_ONLY_ANNOTATION_KEY } from './utils';

export interface AddAnnotationOptions {
  range: Range | BaseSelection;
  key?: AnnotationKey;
  kind?: AnnotationKind;
  annotationProperties?: { [k: string]: any };
}

interface RemoveAnnotationOptions {
  key?: AnnotationKey;
}

interface SelectAnnotationOptions {
  key?: AnnotationKey;
}

interface ResolveAnnotationOptions {
  key: AnnotationKey;
  resolved: boolean;
}

interface UpdateAnnotationOptions {
  key: AnnotationKey;
  newProperties: Partial<Annotation>;
}

interface CreateAnnotationOptions {
  key: AnnotationKey;
  userId?: RecordId;
}

interface AnnotationFilters {
  resolved?: boolean;
}

class Annotations {
  static addAnnotation(editor: Editor, options: AddAnnotationOptions) {
    if (!editor) return;

    const { range, key = LOCAL_ONLY_ANNOTATION_KEY } = options;
    if (!range) return;
    if (!Range.isRange(range)) return;

    const annotation: UnreferencedAnnotation = {
      key,
      range,
      kind: options.kind ?? 'comment',
      wasCut: false,
      ...options.annotationProperties,
    };

    if (annotation.kind === 'comment') {
      Object.assign(annotation, {
        resolved: false,
        hidden: false,
      });
    }

    editor.apply({
      type: 'add_annotation',
      annotation,
    });
  }

  static removeAnnotation(
    editor: Editor,
    options: RemoveAnnotationOptions = {}
  ) {
    if (!editor) return;

    const { key = LOCAL_ONLY_ANNOTATION_KEY } = options;
    const annotation = editor.metadata?.annotations[key];
    if (!annotation) return;
    const unreferencedAnnotation: UnreferencedAnnotation = {
      ...annotation,
      range: annotation.range?.current || null,
    };

    editor.apply({
      type: 'remove_annotation',
      annotation: unreferencedAnnotation,
    });
  }

  static updateAnnotation(editor: Editor, options: UpdateAnnotationOptions) {
    if (!editor) return;

    const { key, newProperties } = options;
    if (!key || !newProperties) return;

    const annotation = editor.metadata?.annotations[key];
    if (!annotation) return;

    const properties = {
      ...annotation,
      range: cloneDeep(omit(annotation.range, 'unref')),
    };

    const unreferencedNewProperties = {
      ...newProperties,
      ...(newProperties.range
        ? { range: cloneDeep(omit(newProperties.range, 'unref')) }
        : {}),
    };

    editor.apply({
      type: 'set_annotation',
      properties,
      newProperties: unreferencedNewProperties,
    });
  }

  // Saves the current properties of all annotations, so they can be
  // restored after an undo. Otherwise, we have to rely on the range
  // affinity which is not always accurate.
  static snapshotAnnotations(editor: Editor) {
    if (!editor) return;

    const { annotations } = editor.metadata || {};
    if (!annotations) return;

    for (const annotation of Object.values(annotations)) {
      if (!annotation) continue;

      Annotations.updateAnnotation(editor, {
        key: annotation.key,
        newProperties: annotation,
      });
    }
  }

  static changeAnnotationResolved(
    editor: Editor,
    options: ResolveAnnotationOptions
  ) {
    const { key, resolved } = options;
    Annotations.updateAnnotation(editor, { key, newProperties: { resolved } });
  }

  static selectAnnotation(editor: Editor, options: SelectAnnotationOptions) {
    const { key } = options;
    if (!key) return;

    const annotation = editor.metadata?.annotations[key];
    if (!annotation) return;

    ReactEditor.focus(editor);
    const rangeRef = annotation.range;
    if (!rangeRef || Range.isRange(rangeRef)) return;

    const range = rangeRef.current;
    if (!range) return;

    const annotationStart = Range.start(range);
    const selection = {
      anchor: annotationStart,
      focus: annotationStart,
    };

    Transforms.select(editor, selection);
  }

  static getAnnotatedText(editor: Editor, annotation: Annotation): string {
    if (!editor) return '';

    const range = annotation?.range?.current;
    if (!range) return '';

    return textInRange(editor, range);
  }

  static isSelected(editor: Editor, annotation: Annotation): boolean {
    if (!editor?.selection) return false;

    const range = annotation?.range?.current;
    if (!range) return false;

    return !!Range.intersection(editor.selection, range);
  }

  static getAnnotations(editor: Editor): Annotation[] {
    if (!editor?.metadata?.annotations) return [];

    const annotations = Object.values<Annotation | undefined>(
      editor.metadata.annotations
    ).filter(Boolean);

    return this.sortAnnotationsByRangeStart(annotations);
  }

  static getAnnotation(editor: Editor, key: string): Annotation | undefined {
    if (!editor?.metadata?.annotations) return;
    if (!isAnnotationKey(key)) return;

    return editor.metadata.annotations[key];
  }

  static getLocalAnnotation(editor: Editor): Annotation | undefined {
    if (!editor?.metadata?.annotations) return;

    return editor.metadata.annotations[LOCAL_ONLY_ANNOTATION_KEY];
  }

  static getAnnotationKeys(editor: Editor): AnnotationKey[] {
    if (!editor) return [];

    const keys = Annotations.getAnnotations(editor)
      .map((annotation) => {
        if (isAnnotationKey(annotation.key)) {
          return annotation.key;
        }
        return null;
      })
      .filter(Boolean);

    return keys;
  }

  static createAnnotation(
    editor: Editor,
    { key, userId }: CreateAnnotationOptions
  ) {
    if (!editor.metadata?.annotations) return;

    const annotation = editor.metadata.annotations[LOCAL_ONLY_ANNOTATION_KEY];
    delete editor.metadata.annotations[LOCAL_ONLY_ANNOTATION_KEY];

    if (!annotation) return;

    const { range, key: oldKey, kind, ...otherProperties } = annotation;

    Annotations.addAnnotation(editor, {
      range: range.current,
      key,
      kind,
      annotationProperties: { userId, ...otherProperties },
    });
  }

  static getAnnotationIdsInRange(
    editor: Editor,
    range: Range | BaseSelection,
    filters: AnnotationFilters = { resolved: false },
    rangeCheck:
      | typeof Range.intersection
      | typeof Range.surrounds = Range.intersection
  ): AnnotationKey[] {
    if (!Range.isRange(range)) return [];

    const annotationsObject = editor.metadata?.annotations;
    if (!annotationsObject) return [];

    const annotations = Object.values<Annotation | undefined>(
      annotationsObject
    ).filter(Boolean);

    const { resolved = false } = filters;
    const annotationIdsInRange: AnnotationKey[] = [];
    for (const annotation of annotations) {
      if (!annotation) continue;
      if (annotation.kind === 'comment' && annotation.resolved !== resolved)
        continue;

      const annotationRange = annotation.range.current;
      if (!annotationRange) continue;

      if (!rangeCheck(range, annotationRange)) continue;

      if (!isAnnotationKey(annotation.key)) continue;

      annotationIdsInRange.push(annotation.key);
    }

    return annotationIdsInRange;
  }

  static getAnnotationIdsForHighlighting(
    editor: Editor,
    range: Range | BaseSelection,
    filters: AnnotationFilters = { resolved: false }
  ) {
    const annotationIdsDirectlyInSelection = this.getAnnotationIdsInRange(
      editor,
      range,
      filters
    );
    let annotationIdsForHighlighting = [...annotationIdsDirectlyInSelection];

    const annotationsInSelection = annotationIdsDirectlyInSelection
      .map((id) => editor.metadata?.annotations[id])
      .filter(Boolean);

    // Nested annotations will cause a gap in highlighting when the cursor is
    // placed in the outer annotation, so we also include them in the
    // list for proper highlighting.
    for (const annotation of annotationsInSelection) {
      if (!annotation) continue;

      const nestedAnnotationIds = this.getAnnotationIdsInRange(
        editor,
        annotation.range.current,
        filters
      ).filter((key) => key !== annotation.key);

      annotationIdsForHighlighting = [
        ...annotationIdsForHighlighting,
        ...nestedAnnotationIds,
      ];
    }

    return annotationIdsForHighlighting;
  }

  static hideAnnotation(editor: Editor, key: AnnotationKey) {
    this.updateAnnotation(editor, {
      key,
      newProperties: { hidden: true },
    });

    AhaEditor.withProvider(
      editor,
      'annotations',
      (provider: AnnotationsProvider) => {
        provider.onAnnotationHidden(key);
      }
    );
  }

  static showAnnotation(editor: Editor, key: AnnotationKey) {
    this.updateAnnotation(editor, {
      key,
      newProperties: { hidden: false },
    });

    AhaEditor.withProvider(
      editor,
      'annotations',
      (provider: AnnotationsProvider) => {
        provider.onAnnotationShown(key);
      }
    );
  }

  static cutAnnotation(editor: Editor, key: AnnotationKey) {
    if (!editor.selection) return;
    if (key === LOCAL_ONLY_ANNOTATION_KEY) return;

    const annotation = editor.metadata?.annotations[key];
    if (!annotation) return;
    const { range } = annotation;

    if (!Range.isRange(range.current)) return;

    // We set the cut status before hiding the annotation to prevent it from being resolved.
    this.updateAnnotation(editor, {
      key,
      newProperties: {
        wasCut: true,
      },
    });

    // Hide the annotation after the content is cut,
    // otherwise the decoration won't be there when
    // setting the clipboard content.
    setImmediate(() => {
      History.withUndoMerging(editor, () => {
        this.updateAnnotation(editor, {
          key,
          newProperties: { hidden: true },
        });
      });

      AhaEditor.withProvider(
        editor,
        'annotations',
        (provider: AnnotationsProvider) => {
          provider.onAnnotationHidden(key);
        }
      );
    });
  }

  static pasteAnnotation(editor: Editor, annotation: Annotation) {
    if (!editor.selection) return;

    const { key } = annotation;
    if (!isAnnotationKey(key)) return;
    if (key === LOCAL_ONLY_ANNOTATION_KEY) return;

    const annotationNodes: NodeEntry<Text>[] = Array.from(
      Editor.nodes(editor, {
        at: {
          anchor: Editor.start(editor, []),
          focus: Editor.end(editor, []),
        },
        match: (e: Node) => {
          const annotationKey: keyof AnnotationMarks = `annotation_${key}`;
          return Text.isText(e) && e.text !== '' && e[annotationKey] === key;
        },
      })
    );

    if (annotationNodes.length === 0) {
      return;
    }

    this.showAnnotation(editor, key);

    // Sometimes the annotation will end up with a hanging element that can
    // potentially extend to unwanted nodes we need to ensure we don't use
    // it for rebuilding the range.
    const startEntry = annotationNodes[0];
    if (!startEntry) return;
    const [, startPath] = startEntry;

    const endEntry = annotationNodes[annotationNodes.length - 1];
    if (!endEntry) return;
    const [endNode, endPath] = endEntry;

    const annotationRange = Editor.unhangRange(editor, {
      anchor: {
        path: startPath,
        offset: 0,
      },
      focus: {
        path: endPath,
        offset: endNode.text.length,
      },
    });

    const newRef = Editor.rangeRef(editor, annotationRange, {
      affinity: 'inward',
    });

    this.updateAnnotation(editor, {
      key,
      newProperties: { range: newRef, wasCut: false },
    });
  }

  static cleanupAnnotationMarks(editor: Editor, key: RecordId) {
    Transforms.setNodes(
      editor,
      {
        [`annotation_${key}`]: null,
        [`annotation_${key}_kind`]: null,
        [`annotation_${key}_resolved`]: null,
        [`annotation_${key}_hidden`]: null,
        [`annotation_${key}_recordType`]: null,
        [`annotation_${key}_recordId`]: null,
        [`annotation_${key}_promptId`]: null,
      },
      {
        at: {
          anchor: Editor.start(editor, []),
          focus: Editor.end(editor, []),
        },
        match: (e: Node) => {
          const annotationKey: keyof AnnotationMarks = `annotation_${key}`;
          return Text.isText(e) && e[annotationKey] === key;
        },
      }
    );
  }

  static sortAnnotationsByRangeStart(annotations: Annotation[]): Annotation[] {
    return [...annotations].sort((a, b) => {
      if (!a?.range.current || !b?.range.current) return -1;

      const [aStart] = Range.edges(a.range.current);
      const [bStart] = Range.edges(b.range.current);

      return Point.compare(aStart, bStart);
    });
  }

  static markAnnotations(editor: Editor): Editor {
    const annotations = this.getAnnotations(editor);
    if (annotations.length === 0) return editor;

    // Adding annotation marks may change the structure of the document. If a path is modified,
    // it could cause an error when trying to operate on the next annotation.
    // To avoid this, we sort the annotations by their range start point and process them in
    // reverse order which should ensure the next annotation's path remains valid.
    const sortedAnnotations =
      this.sortAnnotationsByRangeStart(annotations).reverse();

    for (const annotation of sortedAnnotations) {
      if (!Range.isRange(annotation.range.current)) continue;
      if (annotation.key === LOCAL_ONLY_ANNOTATION_KEY) continue;

      const annotationMarks: Marks = {};

      annotationMarks[`annotation_${annotation.key}`] = annotation.key;
      annotationMarks[`annotation_${annotation.key}_kind`] = annotation.kind;

      switch (annotation.kind) {
        case 'comment': {
          if (annotation.resolved) {
            annotationMarks[`annotation_${annotation.key}_resolved`] =
              annotation.resolved;
          }
          if (annotation.hidden) {
            annotationMarks[`annotation_${annotation.key}_hidden`] =
              annotation.hidden;
          }
          break;
        }
        case 'record': {
          annotationMarks[`annotation_${annotation.key}_recordType`] =
            annotation.recordType;
          annotationMarks[`annotation_${annotation.key}_recordId`] =
            annotation.recordId;
          break;
        }
        case 'prompt': {
          annotationMarks[`annotation_${annotation.key}_promptId`] =
            annotation.promptId;
          break;
        }
      }

      Transforms.setNodes(editor, annotationMarks, {
        at: annotation.range.current,
        match: Text.isText,
        split: true,
      });
    }

    return editor;
  }

  static unmarkAnnotations(editor: Editor): Editor {
    // Gather all text nodes with annotations
    const annotationNodes = Array.from(
      Editor.nodes(editor, {
        at: {
          anchor: Editor.start(editor, []),
          focus: Editor.end(editor, []),
        },
        match: (n) => {
          return (
            Text.isText(n) &&
            Object.keys(n).some((key) => key.startsWith('annotation_'))
          );
        },
      })
    );

    interface AnnotationData {
      key: AnnotationKey;
      kind: AnnotationKind;
      range: Range;
      resolved?: boolean;
      hidden?: boolean;
      recordType?: ReferenceAhaObject['class'];
      recordId?: RecordId;
      promptId?: RecordId;
    }

    const annotations = new Map<AnnotationKey, AnnotationData>();

    // For each found node, build an annotation and put it into the
    // annotations map. If we've already found a node with that
    // annotation key, extend the range to include the new node.
    for (const [node, path] of annotationNodes) {
      if (!Text.isText(node)) continue;

      const text = node.text;
      const annotationKeys = Object.keys(node)
        .map((key) => key.match(/^annotation_(\d+)$/)?.[1])
        .filter(Boolean) as AnnotationKey[];

      for (const annotationKey of annotationKeys) {
        if (!isAnnotationKey(annotationKey)) continue;
        if (annotationKey === LOCAL_ONLY_ANNOTATION_KEY) continue;

        const existingAnnotation = annotations.get(annotationKey);

        annotations.set(annotationKey, {
          key: annotationKey,
          kind: node[`annotation_${annotationKey}_kind`] || 'comment',
          resolved: node[`annotation_${annotationKey}_resolved`] || false,
          hidden: node[`annotation_${annotationKey}_hidden`] || false,
          recordType: node[
            `annotation_${annotationKey}_recordType`
          ] as ReferenceAhaObject['class'],
          recordId: node[`annotation_${annotationKey}_recordId`],
          promptId: node[`annotation_${annotationKey}_promptId`],
          range: {
            anchor: existingAnnotation?.range.anchor ?? {
              path,
              offset: 0,
            },
            focus: { path, offset: text.length },
          },
        });
      }
    }

    const editorAnnotationKeys = new Set(this.getAnnotationKeys(editor));

    // Update the annotation metadata in the editor
    for (const [key, annotation] of annotations) {
      if (editorAnnotationKeys.has(key)) {
        this.updateAnnotation(editor, {
          key,
          newProperties: {
            range: Editor.rangeRef(editor, annotation.range, {
              affinity: 'inward',
            }),
          },
        });
      } else if (annotation.range) {
        this.addAnnotation(editor, {
          key,
          kind: annotation.kind,
          range: annotation.range,
        });
      }

      const newProperties: Partial<Annotation> = {
        kind: annotation.kind,
        wasCut: false,
      };

      switch (newProperties.kind) {
        case 'comment': {
          newProperties.resolved = annotation.resolved;
          newProperties.hidden = annotation.hidden;
          break;
        }
        case 'record': {
          newProperties.recordType = annotation.recordType;
          newProperties.recordId = annotation.recordId;
          break;
        }
        case 'prompt': {
          newProperties.promptId = annotation.promptId;
          break;
        }
      }

      this.updateAnnotation(editor, {
        key,
        newProperties,
      });
    }

    // Remove the annotation marks from the text nodes.
    for (const [key] of annotations) {
      if (key === LOCAL_ONLY_ANNOTATION_KEY) continue;

      this.cleanupAnnotationMarks(editor, key);
    }
    return editor;
  }

  static getEditorForCutting(editor: Editor): Editor {
    const cutAnnotations = this.getAnnotationIdsInRange(
      editor,
      editor.selection,
      { resolved: false },
      // We want the selection to completely surround the annotation in order to
      // cut it, otherwise the annotation will remain on the uncut portion.
      Range.surrounds
    );

    if (cutAnnotations.length === 0) return editor;

    // We need to manipulate the annotated nodes in an off-screen editor,
    // otherwise SlateReact will explode because we updated the node
    // without giving it a chance to update its references.
    const offscreenEditor = createEditor();
    offscreenEditor.children = cloneDeep(editor.children);
    offscreenEditor.selection = cloneDeep(editor.selection);

    // Add the annotation mark to the text nodes contained
    // in the annotation range for the offscreen editor.
    for (const key of cutAnnotations) {
      const annotation = editor.metadata?.annotations[key];
      if (!annotation) continue;
      if (!Range.isRange(annotation.range.current)) continue;
      const marks: Marks = {};
      marks[`annotation_${key}`] = key;

      Transforms.setNodes(offscreenEditor, marks, {
        split: true,
        match: Text.isText,
        at: annotation.range.current,
      });
      this.cutAnnotation(editor, key);
    }

    return offscreenEditor;
  }
}

export default Annotations;
